import type { ApiError as ApiErrorBody } from "@milqit/shared-types";
import { ERROR_CODES, ERROR_MESSAGES, HTTP_STATUS } from "@milqit/messages";

import { env } from "../env";

const API_URL = env.apiUrl;

/** Full URL of an API path, for things fetch doesn't cover (EventSource). */
export const apiUrl = (path: string) => `${API_URL}${path}`;

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** Per-field messages ("rows.3.price") from validation and import errors. */
  readonly fieldErrors?: Record<string, string[]>;
  constructor(status: number, body: Partial<ApiErrorBody>) {
    super(body.message ?? ERROR_MESSAGES.requestFailed);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code ?? ERROR_CODES.UNKNOWN_ERROR;
    this.fieldErrors = body.fieldErrors;
  }
}

/** How long a request may take before people are told the API isn't answering. */
const REQUEST_TIMEOUT_MS = 20_000;

/**
 * `fetch`, with a time limit, turning "no connection" and "no answer" into an ApiError worded for
 * people (status 0) instead of the browser's own "Failed to fetch". A caller's own cancellation
 * (TanStack Query dropping a stale request) passes through untouched.
 */
async function send(url: string, init: RequestInit, timeoutMs = REQUEST_TIMEOUT_MS) {
  const timeout = AbortSignal.timeout(timeoutMs);
  const signal = init.signal ? AbortSignal.any([init.signal, timeout]) : timeout;
  try {
    return await fetch(url, { ...init, signal });
  } catch (error) {
    if (init.signal?.aborted) throw error;
    const timedOut = timeout.aborted;
    throw new ApiError(0, {
      code: timedOut ? ERROR_CODES.TIMEOUT : ERROR_CODES.NETWORK_ERROR,
      message: timedOut ? ERROR_MESSAGES.noAnswer : ERROR_MESSAGES.network,
    });
  }
}

/** A 4xx answer is a decision, not a hiccup: asking again gets the same answer. */
export const isClientError = (error: unknown) =>
  error instanceof ApiError && error.status >= 400 && error.status < 500;

/**
 * Called when the API says the session is gone (401 outside the sign-in endpoints). The auth store
 * registers itself here, so this module doesn't import it and there's no import cycle.
 */
let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(handler: () => void) {
  onUnauthorized = handler;
}

/** Sign-in, refresh and sign-out answer 401 on purpose; never refresh because of them. */
const SESSION_ROUTES = /^\/admin\/auth\/(login|refresh|logout)/;

let refreshing: Promise<boolean> | null = null;

/**
 * Renews the short access cookie from the refresh cookie (httpOnly; the API rotates it). Requests
 * that fail together share one renewal. False when staff have to sign in again.
 */
export function renewSession(): Promise<boolean> {
  refreshing ??= send(`${API_URL}/admin/auth/refresh`, {
    method: "POST",
    // JSON content type: the API refuses refreshes that other sites could send.
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

export interface ApiFetchOptions extends RequestInit {
  /** For slow jobs such as a catalogue import; most calls keep the default. */
  timeoutMs?: number;
}

export async function apiFetch<T>(
  path: string,
  { timeoutMs, ...init }: ApiFetchOptions = {},
): Promise<T> {
  const request = () =>
    send(
      `${API_URL}${path}`,
      {
        ...init,
        credentials: "include",
        headers: {
          Accept: "application/json",
          ...(init.body ? { "Content-Type": "application/json" } : {}),
          ...init.headers,
        },
      },
      timeoutMs,
    );
  let response = await request();
  // An access cookie lasts minutes: renew it once from the refresh cookie and try again.
  if (
    response.status === HTTP_STATUS.UNAUTHORIZED &&
    !SESSION_ROUTES.test(path) &&
    (await renewSession())
  ) {
    response = await request();
  }
  if (!response.ok) {
    // The API session ended (expired, revoked, signed out elsewhere).
    if (response.status === HTTP_STATUS.UNAUTHORIZED && !path.startsWith("/admin/auth/")) {
      onUnauthorized();
    }
    throw new ApiError(
      response.status,
      (await response.json().catch(() => ({}))) as Partial<ApiErrorBody>,
    );
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : null) as T;
}

/** For "get one" calls whose callers treat a missing record as `null`. */
export async function apiFetchOrNull<T>(path: string): Promise<T | null> {
  try {
    return await apiFetch<T>(path);
  } catch (error) {
    if (error instanceof ApiError && error.status === HTTP_STATUS.NOT_FOUND) return null;
    throw error;
  }
}

export function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "" || value === "all") continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : "";
}
